import { Card, CardContent, Grid, Button, CardHeader } from '@mui/material';
import React, { useEffect } from 'react';
import PageTitle from '../../components/PageTitle';
import { LoadingButton } from '@mui/lab';
import { FormProvider, RHFSelectField, RHFTextField } from '../../common/hook-form';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { apiConstants, postGeneric } from '../../common/constants';
import CarrierLogPanels, { fetchCarrierLogs } from './CarrierLogPanels';
import { useLocation } from 'react-router-dom';

const SEARCH_BY_OPTIONS = [
    { value: 'reference', label: 'Reference Number' },
    { value: 'awb', label: 'AWB Number' },
];

export default function ClickPostLogs() {
    const [isLoading, setIsLoading] = React.useState(false);
    const [result, setResult] = React.useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const reference = queryParams.get('reference');
    const awb = queryParams.get('awb');

    const methods = useForm({
        defaultValues: {
            searchBy: 'reference',
            searchValue: '',
        },
    });

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        if (reference) {
            methods.setValue('searchBy', 'reference');
            methods.setValue('searchValue', reference);
            refSearch(reference);
        } else if (awb) {
            methods.setValue('searchBy', 'awb');
            methods.setValue('searchValue', awb);
            awbSearch(awb);
        }
    }, [reference, awb]);

    const { handleSubmit, reset, watch } = methods;
    const searchBy = watch('searchBy');

    const refSearch = async (val) => {
        setIsLoading(true);
        setResult([]);
        try {
            const logs = await fetchCarrierLogs(val);
            if (logs.length > 0) {
                setResult(logs);
                // Scroll to top after results are set to prevent jumping to bottom
                setTimeout(() => {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }, 100);
            } else {
                enqueueSnackbar('No logs found', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error fetching logs', error);
        } finally {
            setIsLoading(false);
        }

    }

    // Carrier logs are keyed by the order's reference number only, so an AWB is first
    // resolved to its order's reference and the logs are then looked up by that.
    const awbSearch = async (val) => {
        setIsLoading(true);
        setResult([]);
        let orderReference;
        try {
            const response = await postGeneric(apiConstants.ORDER_SEARCH, {
                includes: 'reference,awbNumber',
                andfilter: [
                    { field: 'awbNumber', operator: 'eq', value: val }
                ]
            });
            orderReference = response?.data?.data?.[0]?.reference;
        } catch (error) {
            console.error('Error fetching order for AWB', error);
        }
        if (!orderReference) {
            setIsLoading(false);
            enqueueSnackbar('No order found for this AWB', { variant: 'error' });
            return;
        }
        refSearch(orderReference);
    }

    const onSubmit = async (data) => {
        const val = (data.searchValue || '').trim();
        if (!val) {
            enqueueSnackbar(`Please enter ${data.searchBy === 'awb' ? 'an AWB' : 'a reference'} number`, { variant: 'error' });
            return;
        }
        if (data.searchBy === 'awb') {
            awbSearch(val);
        } else {
            refSearch(val);
        }
    };

    return (
        <>
            <PageTitle>Carrier Logs</PageTitle>
            <Grid sx={{ paddingBottom: 2 }}>
                <Card>
                    <CardHeader title="Carrier API Logs"
                        subheader="Search by reference number or AWB number"
                    />
                    <CardContent>
                        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} sx={{ paddingBottom: 3 }}>
                                <Grid item xs={12} md={2}>
                                    <RHFSelectField label="Search By" name="searchBy" options={SEARCH_BY_OPTIONS} width='100%' />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <RHFTextField label={searchBy === 'awb' ? 'AWB Number' : 'Reference Number'} name="searchValue" variant='outlined' />
                                </Grid>
                                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LoadingButton loading={isLoading} variant="contained" type="submit" size='small'>
                                        Submit
                                    </LoadingButton>
                                    <Button sx={{ ml: 1 }} variant="contained" color="error" onClick={() => { reset(); setResult([]); }} size='small'>Reset</Button>
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </CardContent>
                </Card>
            </Grid>

            <CarrierLogPanels logs={result} />
        </>
    );
}
